import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {environment } from '../../environments/environment';
import {AuthService} from './auth.service';
import { concatAll, from, map, Observable } from 'rxjs';

const API_CALL_TYPE = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE'
}


@Injectable()
export class ApiService {
  baseUrl: string;
  authService: AuthService;
  useLocalStorage: boolean = true;
  dialogRef: any;
  //errorMessages: Array<Object>;
  callFailedStatus = 'Status call failed';

  constructor(
    private http: HttpClient,
    private router: Router,
    authService: AuthService
  ) {
    this.baseUrl = environment.baseUrl;
    this.authService = authService;
  }

  getCall(
    url: string,
    isExternal: boolean = false,
    withToken: boolean = true,
    params: any = null,
    extraCallParameters: any = null,
    passErrorResponseAsIs: boolean = false,
    additionalConfigurations: any = null,
    isMultiPartForm: boolean = false
  ): any {
    return this.apiCall(
      API_CALL_TYPE.GET,
      url,
      null,
      isExternal,
      withToken,
      params,
      false,
      extraCallParameters,
      passErrorResponseAsIs,
      additionalConfigurations,
      isMultiPartForm
    );
  }

  postCall(
    url: string,
    data: any,
    isExternal: boolean = false,
    withToken: boolean = true,
    isJson: boolean = false,
    passErrorResponseAsIs: boolean = false,
    extraCallParameters: any = null,
    isMultiPartForm: boolean = false
  ) {
    return this.apiCall(
      API_CALL_TYPE.POST,
      url,
      data,
      isExternal,
      withToken,
      null,
      isJson,
      extraCallParameters,
      passErrorResponseAsIs,
      null,
      isMultiPartForm
    );
  }

  putCall(
    url: string,
    data: any,
    isExternal: boolean = false,
    withToken: boolean = true,
    isJson: boolean = false,
    passErrorResponseAsIs: boolean = false,
    isMultiPartForm: boolean = false
  ) {
    return this.apiCall(
      API_CALL_TYPE.PUT,
      url,
      data,
      isExternal,
      withToken,
      null,
      isJson,
      null,
      passErrorResponseAsIs,
      null,
      isMultiPartForm
    );
  }

  deleteCall(
    url: string,
    isExternal: boolean = false,
    withToken: boolean = true,
    passErrorResponseAsIs: boolean = false,
    isMultiPartForm: boolean
  ) {
    return this.apiCall(
      API_CALL_TYPE.DELETE,
      url,
      null,
      isExternal,
      withToken,
      null,
      false,
      null,
      passErrorResponseAsIs,
      null,
      isMultiPartForm
    );
  }

  patchCall(
    url: string,
    data: any,
    isExternal: boolean = false,
    withToken: boolean = true,
    isJson: boolean = false,
    passErrorResponseAsIs: boolean = false,
    isMultiPartForm: boolean
  ) {
    return this.apiCall(
      API_CALL_TYPE.PATCH,
      url,
      data,
      isExternal,
      withToken,
      null,
      isJson,
      null,
      passErrorResponseAsIs,
      null,
      isMultiPartForm
    );
  }

  apiCall(
      type: string,
      url:string,
      data: any,
      isExternal: boolean = false,
      withToken: boolean = true,
      params = null,
      isJson = false,
      extraCallParameters: any = null,
      passErrorResponseAsIs: boolean = false,
      additionalConfigurations: any = null,
      isMultiPartForm: boolean
  ) {
    if (!isExternal) {
      url = this.baseUrl + url;
    }

    let call: any;
    switch (type) {
      case API_CALL_TYPE.POST:
        call = this.withGetOptions(
          (options) => this.http.post(url, data, options),
          withToken,
          isJson,
          params,
          extraCallParameters,
          isMultiPartForm
        );
        break;
      case API_CALL_TYPE.PUT:
        call = this.withGetOptions(
          (options) => this.http.put(url, data, options),
          withToken,
          isJson,
          params,
          extraCallParameters,
          isMultiPartForm
        );
        break;
      case API_CALL_TYPE.PATCH:
        call = this.withGetOptions(
          (options) => this.http.patch(url, data, options),
          withToken,
          isJson,
          params,
          extraCallParameters,
          isMultiPartForm
        );
        break;
      case API_CALL_TYPE.DELETE:
        call = this.withGetOptions(
          (options) => this.http.delete(url, options),
          withToken,
          isJson,
          params,
          extraCallParameters,
          isMultiPartForm
        );
        break;
      case API_CALL_TYPE.GET:
        call = this.withGetOptions(
          (options) => this.http.get(url, options),
          withToken,
          isJson,
          params,
          extraCallParameters,
          isMultiPartForm
        );
        break;
    }
    return call;
  }

  //async was key
  withGetOptions(
    callbackFn: (options: any) => Observable<any>,
    withToken: boolean = true,
    isJson: boolean = false,
    params: any = null,
    extraCallParameters: any = null,
    isMultiPartForm: boolean = false
  ) {
    return from(
      this.getOptions(
        withToken,
        isJson,
        params,
        extraCallParameters,
        isMultiPartForm
      )
    ).pipe(map(callbackFn), concatAll());
  }

  async getOptions (
    withToken: boolean = true,
    isJson: boolean = false,
    params: any = null,
    extraCallParameters: any = null,
    isMultiPartForm: boolean = false
  ) {
    const httpHeaders: any = {}
    const options: any = {};
    httpHeaders['Access-Control-Allow-Origin'] = '*';
    options['observe'] = 'response';
    options['timeout'] = 90000;

    if (isJson) {
      httpHeaders['Content-Type'] = 'application/json';
    }

    if (withToken) {
      //const token = await this.authService.getAuthToken();
      // @ts-ignore
      let idToken: string = localStorage.getItem("idToken")
      httpHeaders['Authorization'] = 'Bearer ' + idToken;
      //httpHeaders['Authorization'] = 'Bearer ' + '';
    }

    if (this.isObject(params)) {
      options['params'] = this.convertToHttpParams(params);
    }

    // to send the different response type like blob, text, or json
    if (this.isObject(extraCallParameters)) {
      for (const key in extraCallParameters) {
        options['responseType'] = extraCallParameters[key];
      }
    }

    if (this.isObject(httpHeaders)) {
      options['headers'] = httpHeaders;
    }
    return options;
  }

  convertToHttpParams(params: any = {}): {
    [param: string]: string | string[];
  } {
    params = Object.assign({}, params);
    Object.keys(params).forEach((key) => {
      if (this.isObject(params[key])) {
        params[key] = JSON.stringify(params[key]);
      }
    })
    return params;
  }

  isArray(obj: any) {
    return Array.isArray(obj);
  }

  isObject(val: any) {
    if (val === null) {
      return false;
    }
    return typeof val === 'object' && !Array.isArray(val);
  }

  formatDate(inputDate: number): string {
    const date: Date = new Date(inputDate);
    return date.toDateString();
  }

  formatMonth(monthDigit: string): string {
    if (monthDigit == '1') {
      return '01';
    } else if (monthDigit == '2') {
      return '02';
    } else if (monthDigit == '3') {
      return '03';
    } else if (monthDigit == '4') {
      return '04';
    } else if (monthDigit == '5') {
      return '05';
    } else if (monthDigit == '6') {
      return '06';
    } else if (monthDigit == '7') {
      return '07';
    } else if (monthDigit == '8') {
      return '08';
    } else if (monthDigit == '9') {
      return '09';
    } else if (monthDigit == '10') {
      return '10';
    } else if (monthDigit == '11') {
      return '11';
    } else if (monthDigit == '12') {
      return '12';
    } else {
      return '';
    }
  }

  formatZipCode(inputStr: string): string {
    //75126-2401 example --> return as is
    let regexp = new RegExp('^[0-9][0-9][0-9][0-9][0-9]-[0-9][0-9][0-9][0-9]$');
    let test = regexp.test(inputStr);
    if (test) {
      return inputStr;
    }

    //75126 example for 5 digits --> return as is
    let regexp2 = new RegExp('^[0-9][0-9][0-9][0-9][0-9]$');
    let test2 = regexp2.test(inputStr);
    if (test2) {
      return inputStr;
    }

    //751262401 -> return as 75126-2401
    let regexp3 = new RegExp('^[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9]$');
    let test3 = regexp3.test(inputStr);
    if (test3) {
      let first5digits: string = inputStr.slice(0,5);
      let last4digits: string = inputStr.slice(5)
      let formatedZipCode = first5digits + '-' + last4digits;
      return formatedZipCode;
    }
    //Unknown Zip code
    return inputStr
  }

  formatEin(inputStr: string): string {
    if (inputStr == 'N/A' || !inputStr) {
      return 'N/A';
    }
    if (inputStr) {
      let first2Digits: string = inputStr.slice(0,2);
      let lastDigits: string = inputStr.slice(2);
      return first2Digits + '-' + lastDigits;
    }
    return inputStr
  }

  formatPhoneNumber(inputStr: string):string {
    if (inputStr) {
      if ((inputStr.length == 10 || (inputStr.length == 11 && inputStr[0] == '1'))) {
        //USA formatted Phone Number default
        let first3Digits: string = inputStr.slice(0, 3);
        let mid3Digits: string = inputStr.slice(3, 6);
        let lastDigits: string = inputStr.slice(6);
        let formattedPhoneNumber = first3Digits + '-' + mid3Digits + '-' + lastDigits;
        return formattedPhoneNumber;
      }
      return inputStr
      //Maybe extend to other countries but this may be unlikely since we are dealing the primarily US donor
    }
    return 'N/A';
  }

  sortTextAsc(columnName: string, rows: any[]): any[] {
    rows.sort((a, b) => {
      if (a[columnName] < b[columnName]) {
        return -1;
      } else if (a[columnName] > b[columnName]) {
        return 1;
      } else {
        return 0;
      }
    });
    return rows
  }

  sortTextDesc(columnName: string, rows: any[]): any[] {
    rows.sort((a, b) => {
      if (a[columnName] < b[columnName]) {
        return 1;
      } else if (a[columnName] > b[columnName]) {
        return -1;
      } else {
        return 0;
      }
    });
    return rows
  }

  convertDateStringToTimestamp(dateString: string): number {
    const date = new Date(dateString);
    return date.getTime();
  }

  sortDateAsc(columnName: string, rows: any[]): any[] {

    rows.sort((a, b) => {
      let aDate: number = this.convertDateStringToTimestamp(a[columnName])
      let bDate: number = this.convertDateStringToTimestamp(b[columnName])
      if (aDate < bDate) {
        return -1;
      } else if (aDate > bDate) {
        return 1;
      } else {
        return 0;
      }
    });
    return rows
  }

  sortDateDesc(columnName: string, rows: any[]): any[] {
    rows.sort((a, b) => {
      let aDate: number = this.convertDateStringToTimestamp(a[columnName])
      let bDate: number = this.convertDateStringToTimestamp(b[columnName])
      if (aDate < bDate) {
        return 1;
      } else if (aDate > bDate) {
        return -1;
      } else {
        return 0;
      }
    });
    return rows
  }

  isNumber(value: any) {
    return typeof value === 'number';
  }

  isString(value: any) {
    return typeof value === 'string';
  }

  escapeOutput(toOutput: string){
    return toOutput.replace(/\&/g, '&amp;')
      .replace(/\</g, '&lt;')
      .replace(/\>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/\'/g, '&#x27;')
      .replace(/\//g, '&#x2F;')
      .replace(/\%/g, '&pct;')
      .replace(/;/g, '');
  }

  removeControlChar(toOutput: string){
    return toOutput.replace(/\&/g, '')
      .replace(/\</g, '')
      .replace(/\>/g, '')
      .replace(/\"/g, '')
      .replace(/\'/g, '')
      .replace(/\//g, '')
      .replace(/\%/g, '')
      .replace(/;/g, '');
  }

  trimLeadingTrailingSpaces(inputStr: string): string {
    let finalStr: string = inputStr;
    finalStr = inputStr.replaceAll(/^\s+|\s+$/g,'');
    return finalStr;
  }

  calculateReadOnly(url: string, impersonatedEmail: string, roleName: string): boolean {
    if (url.endsWith("view") && impersonatedEmail && roleName && roleName.endsWith("READ_ONLY_ACCESS")) {
      return true;
    }
    return false;
  }
}
